import React from 'react';
import { Layout } from '@/components/Layout';
import { Col, Container } from '@visalex/core';

import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import styles from '@/components/Terms/Terms.module.scss';

const PrivacyPolicy = () => {
  const { t } = useTranslation();

  return (
    <Layout title={t('privacy-policy.seo-title')} description={t('privacy-policy.seo-description')}>
      <Container className={styles.terms}>
        <Col>
          <h1>OUR COMMITMENT TO PRIVACY</h1>
          <p>
            VisaLex, LLC (“VisaLex”, “we” or “us”) is committed to protecting the privacy of all users of our services,
            which may include business organizations and their individual employees or potential employees or service
            providers (“you”). We are providing this Privacy Policy to communicate our policies with respect to your
            information when you use our website located at visalex.com and related mobile applications (collectively,
            the “Platform”) or any VisaLex services made available through the Platform, including technology, tools,
            and administrative support (the “Support Services”).
          </p>
          <p>
            Please review this policy to understand the steps VisaLex has taken to secure your Personal Information.
          </p>

          <h2>INFORMATION WE COLLECT AND HOW WE USE IT</h2>

          <h3>Information You Give Us</h3>
          <p>
            We refer to information that can personally identify you, such as your name, address and email address as
            “Personal Information”. While you may visit the Platform without providing Personal Information, when you
            use the Support Services, you may be required to provide certain Personal Information to allow VisaLex to
            provide the Support Services and to allow Authorized Representatives and Attorneys to process Matters on
            your Employer’s behalf (see further details below). “Authorized Representatives” are (i) local
            representatives engaged to assist with Matters in countries outside the United States and (ii) third
            parties, including government agencies, to whom VisaLex or the Attorneys or Authorized Representatives are
            authorized to make disclosures for purposes of pursuing Matters. “Attorneys” are, collectively, the
            attorneys and such attorneys’ support staff at American Immigration Associates, LLC (“AIA LLC”), an Illinois
            limited liability company, and other attorneys and attorney support staff who have agreed to provide legal
            services to process the immigration matters on behalf of our clients’ employees or potential employees
            (“Matters”). “Employer” means either your employer or prospective employer.
          </p>
          <p>
            If VisaLex is helping your Employer seek permission for you to work in the United States, VisaLex is
            providing its Support Services to your Employer, and the Attorneys are providing your Employer with legal
            assistance. While VisaLex and the Attorneys do not represent you, as an individual, in order to complete the
            process for applying for a work authorization, your Employer will need you to provide certain information to
            the Attorneys. VisaLex collects and processes this information (including any of your Personal Information)
            on behalf of your Employer and we may use your Personal Information to provide the Support Services and to
            facilitate the Attorneys’ and their Authorized Representatives’ processing of their Matters. If you have any
            questions as to how this information (including any of your Personal Information) is used by your Employer
            (or on behalf of your Employer), please raise these with your Employer.
          </p>
          <p>
            If you do provide VisaLex with information (including Personal Information) as part of the Support Services,
            you hereby consent to the use of such information for us to provide the Support Services and as part of the
            process of applying for the work authorization requested by your Employer.
          </p>
          <p>
            We may also use your Personal Information to contact you about our services or information that may interest
            you, and to provide you with information you have requested (such as if you contact us about the services we
            provide, or if you sign up for our email blog updates). You can opt-out of these communications by
            unsubscribing to such emails.
          </p>
          <p>
            Except as specifically stated in this Privacy Policy, we do not disclose your Personal Information to third
            parties.
          </p>

          <h3>Usage Information</h3>
          <p>
            We collect non-personal statistical information that does not personally identify you. This includes
            non-personal statistical information about your internet connection, the equipment that you use to access
            our Platform, and how you use the Platform. We refer to this information as “Usage Information”. This
            information allows us to optimize the Platform to the internet connections and equipment that are used to
            access the Platform. We collect Usage Information directly from you when you provide it to us and
            automatically as you navigate through and interact with our Platform. Information collected this way
            includes: (i) details of your visits to our Platform, including traffic data, location data, logs, other
            communication data, and the resources that you access and use on the Platform; and (ii) information about
            your computer and internet connection, including your IP address, operating system, and browser type. The
            technologies we use for this automatic data collection may include:
          </p>
          <ul>
            <li>
              <b>Cookies (or browser cookies):</b> A cookie is a small file placed on the hard drive of your computer.
              We use session cookies to keep track of your login information. You may refuse to accept browser cookies
              by activating the appropriate setting on your browser. However, if you select this setting, you may be
              unable to access certain parts of our Platform. Unless you have adjusted your browser setting so that it
              will refuse cookies, our system will issue cookies when you use the Platform.
            </li>
            <li>
              <b>Pixel Tags/ Web Beacons:</b> Webpages on our Platform and our emails may contain small electronic files
              known as web beacons (also referred to as clear gifs. pixel tags and single-pixel gifs) that permit us,
              for example, to count users who have visited those pages or opened an email. We may also use these web
              beacons for other related Platform statistics (e.g., recording the popularity of certain content and
              verifying system and server integrity).
            </li>
            <li>
              <b>Device Identifiers:</b> When you use a mobile device like a tablet or phone to access our Platform, we
              may access, collect, monitor, store on your device, and/or remotely store one or more “device
              identifiers”. Device identifiers are small data files or similar data structures stored on or associated
              with your mobile device, which uniquely identify your mobile device. A device identifier may be data
              stored in connection with the device hardware, data stored in connection with the device’s operating
              system or other software, or data sent to the device by us.
            </li>
            <li>
              <b>Tracking URLs:</b> These are links that allow VisaLex to determine the effectiveness of a particular
              part of the Platform. For instance, emails from a marketing campaign may include tracking URLs to help
              VisaLex see how popular the page is. This helps us serve more relevant content and information.
            </li>
          </ul>
          <p>
            Whilst we collect this Usage Information to optimize the Platform and monitor and improve our Support
            Services, we do not use these technologies to collect information that would identity you, as an individual,
            or use the Usage Information to profile identifiable individuals or to monitor the behavior of identifiable
            individuals.
          </p>

          <h3>Third-party Service Providers</h3>
          <p>
            We may use subcontractors, vendors or other third-parties in order to efficiently provide the Platform and
            our Support Services (“Service Providers”). Service Providers may include, but are not limited to, those
            providing data hosting and credit card processing. Some Service Providers will collect information directly
            from you. Information collected directly from these Service Providers is governed by their privacy policies.
            You should view the privacy policies of Service Providers before providing information to them.
          </p>

          <h2>WHEN AND HOW WE SHARE YOUR INFORMATION</h2>
          <h3>Sharing Information</h3>
          <p>
            We may disclose Usage Information and aggregated information about our users without restriction. This
            information cannot be used to personally identify our users. We do not disclose your Personal Information
            except in the circumstances described here:
          </p>
          <ul>
            <li>
              <b>Your Consent:</b> We may share or disclose your Personal Information in connection with our provision
              of the Support Services. Specifically, we will share your Personal Information with Attorneys and
              Authorized Representatives in connection with processing of Matters on behalf of your Employer.
            </li>
            <li>
              <b>Service Providers:</b> We may share or disclose your Personal Information with Service Providers in
              order to provide or maintain the Platform and as part of VisaLex’s provision of the Support Services.
            </li>
            <li>
              <b>Business Transfers:</b> In the event that VisaLex is involved in a merger, divestiture, restructuring,
              reorganization, dissolution, or other sale or transfer of some or all of our assets, whether as a going
              concern or as part of bankruptcy, liquidation, or similar proceeding, your Personal Information held by us
              may be among the assets transferred.
            </li>
            <li>
              <b>Law and Harm:</b> Notwithstanding anything to the contrary in this Privacy Policy, we may disclose or
              share your Personal Information in order to comply with any court order, law, or legal process, including
              to respond to any government, public authority or regulatory request, or if we believe disclosure is
              necessary or appropriate to protect the rights, property, or safety of VisaLex, our customers, or others.
              This includes exchanging Personal Information with other companies and organizations for the purposes of
              fraud protection and credit risk reduction. In certain situations, we may be required to disclose personal
              data in response to lawful requests by public authorities, including to meet national security or law
              enforcement requirements.
            </li>
            <li>
              <b>Enforcement:</b> We may disclose your Personal Information in order to enforce or apply our Terms and
              other agreements, including for billing and collection purposes.
            </li>
            <li>
              <b>Disclosed when Collected:</b> For any other purpose disclosed by us when you provide the Personal
              Information.
            </li>
          </ul>

          <h2>YOUR CHOICES ABOUT YOUR INFORMATION</h2>
          <p>
            We strive to provide you with choices regarding the information you provide to us. We have created
            mechanisms to provide you with the following control over your information:
          </p>
          <ul>
            <li>
              <b>Tracking Technologies and Advertising.</b> You can set your browser to refuse all or some browser
              cookies, or to alert you when cookies are being sent. If you disable or refuse cookies, please note that
              some parts of the Platform may then be inaccessible or not function properly.
            </li>
            <li>
              <b>Email Newsletters from VisaLex.</b> If you do not wish to have your contact information used by VisaLex
              to notify you of updates and educational opportunities related to immigration matters or to promote our
              own or third parties’ products or services, you can opt-out by unsubscribing to such emails.
            </li>
          </ul>

          <h3>Accessing and Correcting Your Information</h3>
          <p>
            VisaLex acknowledges the right of access by EU individuals. If you are a registered user of our Support
            Services, you can review and change your Personal Information by logging into the Platform and visiting your
            account’s My Login page.
          </p>
          <p>
            If you are an authorized representative an Employer you may also contact your account manager or send us an
            email at <a href="mailto:privacy@visalex.com">privacy@visalex.com</a> to request access to, correct or
            delete any Personal Information that you have provided to us.
          </p>
          <p>
            Please note that if you delete some or all of your Personal Information, we may not be able to provide the
            Support Services and the Attorneys and Authorized Representatives may not be able to process the relevant
            Matters. We may not accommodate a request to change Personal Information if we believe the change would
            violate any law or legal requirement or cause the Personal Information to be incorrect.
          </p>

          <h3>Your California Privacy Rights</h3>
          <p>
            California Civil Code Section § 1798.83 permits users of our Platform that are California residents to
            request certain information regarding our disclosure of Personal Information to third parties for their
            direct marketing purposes. To make such a request, please send an email to{' '}
            <a href="mailto:privacy@visalex.com">privacy@visalex.com</a>.
          </p>

          <h2>HOW WE PROTECT YOUR INFORMATION</h2>
          <h3>Data Security</h3>
          <p>
            We have implemented measures designed to secure Personal Information from accidental loss and from
            unauthorized access, use, alteration, and disclosure. Additional details about the specific technologies
            used to secure Personal Information are available upon request by sending an email to{' '}
            <a href="mailto:privacy@visalex.com">privacy@visalex.com</a>.
          </p>
          <p>
            The safety and security of your information also depends on you. Where we have given you (or where you have
            chosen) a password for access to certain parts of our Platform, you are responsible for keeping this
            password confidential. We ask you not to share your password with anyone.
          </p>
          <p>
            Unfortunately, the transmission of information via the internet is not completely secure. Although we do our
            best to protect your Personal Information, we cannot guarantee the security of your Personal Information
            transmitted to our Platform.
          </p>
          <p>
            We operate the Platform and manage the provision of the Support Services from the United States. If you are
            located outside of the United States, please be aware that information we collect will be transferred to and
            processed in the United States. By using the Platform, or providing us with any information, you fully
            understand and unambiguously consent to this transfer, processing and storage of your information in the
            United States, a jurisdiction in which the privacy laws may not be as comprehensive as those in the country
            where you reside and/or are a citizen.
          </p>

          <h2>CHANGES TO OUR PRIVACY POLICY</h2>
          <p>
            It is our policy to post any changes we make to our Privacy Policy on this page. If we make material changes
            to how we treat our users’ Personal Information, as determined in our sole discretion, we will notify you by
            email to the email address specified in your registered account and/or through a notice on the Platform.
          </p>
          <p>
            You are responsible for ensuring we have an up-to-date active and deliverable email address for you (where
            you are a registered user of our Platform), and for periodically visiting the Platform and this Privacy
            Policy to check for any changes.
          </p>
          <h3>Contact Information</h3>
          <p>
            To ask questions or concerns about this Privacy Policy and our privacy practices, please contact us at{' '}
            <a href="mailto:privacy@visalex.com">privacy@visalex.com</a>.
          </p>
        </Col>
      </Container>
    </Layout>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}
export default PrivacyPolicy;
